import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Building2,
  FilePlus2,
  Globe2,
  Languages,
  ListTodo,
  LogOut,
  Monitor,
  Moon,
  ReceiptText,
  Search,
  Sun,
} from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  Autocomplete,
  Dialog,
  Header,
  Input,
  Menu,
  MenuItem,
  MenuSection,
  Modal,
  ModalOverlay,
  SearchField,
  useFilter,
} from 'react-aria-components';
import { meQuery } from '../../api/queries';
import { useI18n } from '../../i18n/I18nProvider';
import { useAccess } from '../access';
import { useSignOut } from '../session';
import { useTheme } from '../theme';
import styles from './Shell.module.css';

interface PaletteValue {
  open: () => void;
}

const PaletteContext = createContext<PaletteValue | null>(null);

export function usePalette(): PaletteValue {
  const value = useContext(PaletteContext);
  if (!value) throw new Error('usePalette() must be used inside <CommandPaletteProvider>');
  return value;
}

interface Command {
  id: string;
  label: string;
  keywords?: string;
  icon: ReactNode;
  run: () => void;
}

interface CommandSection {
  id: string;
  title: string;
  commands: Command[];
}

/** Ctrl/Cmd+K opens a palette to jump to pages and start documents (keyboard first). */
export function CommandPaletteProvider({
  orgId,
  children,
}: {
  orgId?: string | undefined;
  children: ReactNode;
}) {
  const [isOpen, setOpen] = useState(false);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const open = useCallback(() => setOpen(true), []);
  const value = useMemo(() => ({ open }), [open]);

  return (
    <PaletteContext.Provider value={value}>
      {children}
      <ModalOverlay
        isOpen={isOpen}
        onOpenChange={setOpen}
        isDismissable
        className={styles.paletteOverlay}
      >
        <Modal className={styles.paletteModal}>
          {isOpen ? <PaletteDialog orgId={orgId} onDone={() => setOpen(false)} /> : null}
        </Modal>
      </ModalOverlay>
    </PaletteContext.Provider>
  );
}

function PaletteDialog({ orgId, onDone }: { orgId?: string | undefined; onDone: () => void }) {
  const { t, locale, setLocale } = useI18n();
  const { setTheme } = useTheme();
  const navigate = useNavigate();
  const signOut = useSignOut();
  const { contains } = useFilter({ sensitivity: 'base' });
  const { data: me } = useQuery(meQuery);
  const access = useAccess(orgId);

  const sections = useMemo<CommandSection[]>(() => {
    const result: CommandSection[] = [];
    // IA.md §1: the palette opens every hub and starts any document, wherever the user is.
    // Hub and document commands are added here as their blocks ship (same permissions as the bar).
    if (orgId && access.can('organization.view')) {
      const go: Command[] = [
        {
          id: 'go-today',
          label: t.nav.today,
          keywords: 'today queue home اليوم الرئيسية',
          icon: <ListTodo size={16} />,
          run: () => void navigate({ to: '/orgs/$orgId', params: { orgId } }),
        },
      ];
      if (access.can('invoice.view'))
        go.push({
          id: 'go-billing',
          label: t.nav.billing,
          keywords: 'billing invoices documents فوترة فواتير مستندات',
          icon: <ReceiptText size={16} />,
          run: () => void navigate({ to: '/orgs/$orgId/sales/invoices', params: { orgId } }),
        });
      result.push({ id: 'go', title: t.palette.sectionGo, commands: go });
    }
    if (orgId && access.can('invoice.create')) {
      result.push({
        id: 'start',
        title: t.sales.newMenu,
        commands: [
          {
            id: 'start-invoice',
            label: t.sales.newInvoice,
            keywords: 'new invoice create فاتورة إنشاء بدء',
            icon: <FilePlus2 size={16} />,
            run: () => void navigate({ to: '/orgs/$orgId/sales/invoices/new', params: { orgId } }),
          },
          {
            id: 'start-export-invoice',
            label: t.sales.newExportInvoice,
            keywords: 'export invoice تصدير',
            icon: <Globe2 size={16} />,
            run: () =>
              void navigate({
                to: '/orgs/$orgId/sales/invoices/new',
                params: { orgId },
                search: { kind: 'ei' },
              }),
          },
        ],
      });
    }

    const orgs: Command[] = (me?.organizations ?? [])
      .filter((org) => org.id !== orgId)
      .map((org) => ({
        id: `org-${org.id}`,
        label: t.palette.switchTo(org.name),
        icon: <Building2 size={16} />,
        run: () => void navigate({ to: '/orgs/$orgId', params: { orgId: org.id } }),
      }));
    orgs.push({
      id: 'org-new',
      label: t.shell.createOrganization,
      icon: <Building2 size={16} />,
      run: () => void navigate({ to: '/orgs/new' }),
    });
    result.push({ id: 'orgs', title: t.palette.sectionOrganizations, commands: orgs });

    result.push({
      id: 'prefs',
      title: t.palette.sectionPreferences,
      commands: [
        {
          id: 'lang',
          label: locale === 'ar' ? t.palette.switchToEnglish : t.palette.switchToArabic,
          keywords: 'language lang english arabic لغة عربي انجليزي',
          icon: <Languages size={16} />,
          run: () => setLocale(locale === 'ar' ? 'en' : 'ar'),
        },
        {
          id: 'theme-light',
          label: t.palette.themeLight,
          icon: <Sun size={16} />,
          run: () => setTheme('light'),
        },
        {
          id: 'theme-dark',
          label: t.palette.themeDark,
          icon: <Moon size={16} />,
          run: () => setTheme('dark'),
        },
        {
          id: 'theme-system',
          label: t.palette.themeSystem,
          icon: <Monitor size={16} />,
          run: () => setTheme('system'),
        },
        {
          id: 'sign-out',
          label: t.shell.signOut,
          icon: <LogOut size={16} />,
          run: () => signOut.mutate(),
        },
      ],
    });
    return result;
  }, [orgId, access, t, navigate, me, locale, setLocale, setTheme, signOut]);

  const byId = useMemo(() => {
    const map = new Map<string, Command>();
    for (const section of sections)
      for (const command of section.commands) map.set(command.id, command);
    return map;
  }, [sections]);

  return (
    <Dialog aria-label={t.palette.title} className={styles.paletteDialog}>
      <Autocomplete filter={contains}>
        <SearchField aria-label={t.palette.searchLabel} autoFocus className={styles.paletteSearch}>
          <Search size={18} aria-hidden="true" />
          <Input placeholder={t.palette.placeholder} />
        </SearchField>
        <Menu
          className={styles.paletteMenu}
          aria-label={t.palette.title}
          onAction={(key) => {
            const command = byId.get(String(key));
            if (!command) return;
            onDone();
            command.run();
          }}
          renderEmptyState={() => <div className={styles.paletteEmpty}>{t.palette.empty}</div>}
        >
          {sections.map((section) => (
            <MenuSection key={section.id} id={section.id}>
              <Header className={styles.paletteSectionHeader}>{section.title}</Header>
              {section.commands.map((command) => (
                <MenuItem
                  key={command.id}
                  id={command.id}
                  textValue={`${command.label} ${command.keywords ?? ''}`}
                  className={styles.menuItem}
                >
                  <span aria-hidden="true" style={{ display: 'inline-flex' }}>
                    {command.icon}
                  </span>
                  <span className={styles.menuItemTitle}>{command.label}</span>
                </MenuItem>
              ))}
            </MenuSection>
          ))}
        </Menu>
      </Autocomplete>
      <div className={styles.paletteFooter} aria-hidden="true">
        <span>↑↓ {t.palette.hintNavigate}</span>
        <span>↵ {t.palette.hintRun}</span>
        <span>Esc {t.palette.hintClose}</span>
      </div>
    </Dialog>
  );
}
